// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* CompareFit.css
 * ────────────────────────────────────────────────────────────────────────
 * Dedicated stylesheet for the Compare Fit tab (PanelCompare in
 * DataFittingPage.jsx). Structural patterns (card spacing scale, panel
 * title treatment, responsive breakpoints) follow Dashboard.css's
 * conventions, adapted to this app's existing DARK theme — using the same
 * CSS custom properties already defined in DataFittingPage.css
 * (--color-surface, --color-border, --color-text, --color-accent, etc.)
 * rather than Dashboard.css's light-theme hex values, so this stays in
 * sync with the rest of Data Fitting automatically if the theme changes.
 *
 * BUG FIX (alignment broke at 100% browser zoom, looked fine at 50%):
 * root cause was a second, redundant \`overflow:auto\` + \`height:100%\`
 * wrapper nested inside PanelShell's own scroll container in the JSX —
 * two auto-scrolling containers with a percentage height resolving
 * against an already-auto-sized ancestor is exactly the kind of thing
 * that clips content inconsistently across zoom levels (subpixel
 * rounding), rather than failing the same way every time. That's fixed
 * in the JSX (single scroll owner: PanelShell). This stylesheet's job is
 * making sure everything below it lays out predictably at ANY zoom level
 * and viewport width — proper wrapping, min-widths, and explicit
 * scrollbars on the two places that can legitimately overflow (the fit
 * picker list, and the summary table on narrow viewports) instead of
 * relying on things "just happening to fit."
 * ──────────────────────────────────────────────────────────────────────── */

.cf-root {
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* No height/overflow here — PanelShell's own content div already owns
     scrolling for this tab. This is just a plain flex column that grows
     to whatever height its content needs. */
}

/* ─── Fit picker ─────────────────────────────────────────────────────── */

.cf-empty-note {
  font-size: 13px;
  color: var(--color-text-dim);
  padding: 8px 2px;
  line-height: 1.5;
}

.cf-picker-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  /* Own scrollbar once the list gets long, instead of pushing the chart
     and table further and further down the page (or, worse, clipping
     silently the way the old nested-overflow bug did). */
  max-height: 220px;
  overflow-y: auto;
  padding-right: 2px;
}

.cf-pick-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  min-width: 0;
}

.cf-pick-row--sel {
  background: var(--color-panel);
}

.cf-pick-row input[type='checkbox'] {
  flex-shrink: 0;
}

.cf-color-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.cf-pick-label {
  color: var(--color-text);
  flex: 1 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cf-pick-model {
  color: var(--color-text-dim);
  font-size: 11px;
  text-transform: uppercase;
  flex-shrink: 0;
}

.cf-pick-active {
  color: var(--color-accent);
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.cf-clear-row {
  margin-top: 8px;
}

.cf-clear-btn {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  color: var(--color-text-muted);
  font-size: 12px;
  padding: 4px 10px;
  cursor: pointer;
}
.cf-clear-btn:hover {
  border-color: var(--color-border-light);
  color: var(--color-text);
}

.cf-select-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  color: var(--color-text-dim);
  font-size: 13px;
  text-align: center;
}

/* ─── Overlay chart ──────────────────────────────────────────────────── */

.cf-warning {
  font-size: 12px;
  color: #d97706;
  background: rgba(217, 119, 6, 0.08);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
  line-height: 1.5;
}

.cf-species-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.cf-species-tab {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
}
.cf-species-tab:hover {
  border-color: var(--color-border-light);
  color: var(--color-text);
}
.cf-species-tab--active {
  border-color: var(--color-accent);
  background: var(--color-accent-glow);
  color: var(--color-accent);
}

/* Fixed, predictable chart height at every zoom level and viewport width
   — width still comes from ResponsiveContainer, only height is pinned. */
.cf-chart-wrap {
  width: 100%;
  height: 320px;
  min-width: 0;
}
@media (max-width: 900px) {
  .cf-chart-wrap { height: 260px; }
}

.cf-footnote {
  font-size: 11px;
  color: var(--color-text-dim);
  margin-top: 6px;
  line-height: 1.5;
}

/* ─── Summary table ──────────────────────────────────────────────────── */

.cf-table-wrap {
  /* The one place a wide comparison (up to 4 fits, plus a growing list of
     per-species R² rows) is allowed to overflow — with its own explicit
     horizontal scrollbar, never a silent clip. */
  overflow-x: auto;
}

.cf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.cf-table th,
.cf-table td {
  padding: 6px 10px;
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
}

.cf-table th {
  text-align: left;
  font-weight: 600;
}

.cf-table td {
  color: var(--color-text);
  white-space: normal;
}

.cf-td-label {
  color: var(--color-text-muted);
  white-space: nowrap;
}

/* ─── Responsive ─────────────────────────────────────────────────────── */

@media (max-width: 560px) {
  .cf-picker-list { max-height: 160px; }
  .cf-pick-model { display: none; } /* keep rows from wrapping awkwardly on narrow phones */
}
`, "",{"version":3,"sources":["webpack://./src/styles/CompareFit.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;;;;;;;;;6EAwB6E;;AAE7E;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;EACT;;4CAE0C;AAC5C;;AAEA,2EAA2E;;AAE3E;EACE,eAAe;EACf,4BAA4B;EAC5B,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR;;yDAEuD;EACvD,iBAAiB;EACjB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;EAChB,kBAAkB;EAClB,eAAe;EACf,eAAe;EACf,YAAY;AACd;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,UAAU;EACV,WAAW;EACX,kBAAkB;EAClB,cAAc;AAChB;;AAEA;EACE,wBAAwB;EACxB,SAAO;EACP,YAAY;EACZ,gBAAgB;EAChB,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,4BAA4B;EAC5B,eAAe;EACf,yBAAyB;EACzB,cAAc;AAChB;;AAEA;EACE,0BAA0B;EAC1B,eAAe;EACf,gBAAgB;EAChB,cAAc;AAChB;;AAEA;EACE,eAAe;AACjB;;AAEA;EACE,gBAAgB;EAChB,qCAAqC;EACrC,kBAAkB;EAClB,8BAA8B;EAC9B,eAAe;EACf,iBAAiB;EACjB,eAAe;AACjB;AACA;EACE,uCAAuC;EACvC,wBAAwB;AAC1B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;EAClB,4BAA4B;EAC5B,eAAe;EACf,kBAAkB;AACpB;;AAEA,2EAA2E;;AAE3E;EACE,eAAe;EACf,cAAc;EACd,mCAAmC;EACnC,qCAAqC;EACrC,kBAAkB;EAClB,iBAAiB;EACjB,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,eAAe;EACf,mBAAmB;AACrB;;AAEA;EACE,iBAAiB;EACjB,kBAAkB;EAClB,eAAe;EACf,eAAe;EACf,qCAAqC;EACrC,uBAAuB;EACvB,8BAA8B;AAChC;AACA;EACE,uCAAuC;EACvC,wBAAwB;AAC1B;AACA;EACE,iCAAiC;EACjC,oCAAoC;EACpC,0BAA0B;AAC5B;;AAEA;yEACyE;AACzE;EACE,WAAW;EACX,aAAa;EACb,YAAY;AACd;AACA;EACE,iBAAiB,aAAa,EAAE;AAClC;;AAEA;EACE,eAAe;EACf,4BAA4B;EAC5B,eAAe;EACf,gBAAgB;AAClB;;AAEA,2EAA2E;;AAE3E;EACE;;iDAE+C;EAC/C,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,yBAAyB;EACzB,eAAe;AACjB;;AAEA;;EAEE,iBAAiB;EACjB,4CAA4C;EAC5C,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,wBAAwB;EACxB,mBAAmB;AACrB;;AAEA;EACE,8BAA8B;EAC9B,mBAAmB;AACrB;;AAEA,2EAA2E;;AAE3E;EACE,kBAAkB,iBAAiB,EAAE;EACrC,iBAAiB,aAAa,EAAE,EAAE,uDAAuD;AAC3F","sourcesContent":["/* CompareFit.css\n * ────────────────────────────────────────────────────────────────────────\n * Dedicated stylesheet for the Compare Fit tab (PanelCompare in\n * DataFittingPage.jsx). Structural patterns (card spacing scale, panel\n * title treatment, responsive breakpoints) follow Dashboard.css's\n * conventions, adapted to this app's existing DARK theme — using the same\n * CSS custom properties already defined in DataFittingPage.css\n * (--color-surface, --color-border, --color-text, --color-accent, etc.)\n * rather than Dashboard.css's light-theme hex values, so this stays in\n * sync with the rest of Data Fitting automatically if the theme changes.\n *\n * BUG FIX (alignment broke at 100% browser zoom, looked fine at 50%):\n * root cause was a second, redundant `overflow:auto` + `height:100%`\n * wrapper nested inside PanelShell's own scroll container in the JSX —\n * two auto-scrolling containers with a percentage height resolving\n * against an already-auto-sized ancestor is exactly the kind of thing\n * that clips content inconsistently across zoom levels (subpixel\n * rounding), rather than failing the same way every time. That's fixed\n * in the JSX (single scroll owner: PanelShell). This stylesheet's job is\n * making sure everything below it lays out predictably at ANY zoom level\n * and viewport width — proper wrapping, min-widths, and explicit\n * scrollbars on the two places that can legitimately overflow (the fit\n * picker list, and the summary table on narrow viewports) instead of\n * relying on things \"just happening to fit.\"\n * ──────────────────────────────────────────────────────────────────────── */\n\n.cf-root {\n  display: flex;\n  flex-direction: column;\n  gap: 14px;\n  /* No height/overflow here — PanelShell's own content div already owns\n     scrolling for this tab. This is just a plain flex column that grows\n     to whatever height its content needs. */\n}\n\n/* ─── Fit picker ─────────────────────────────────────────────────────── */\n\n.cf-empty-note {\n  font-size: 13px;\n  color: var(--color-text-dim);\n  padding: 8px 2px;\n  line-height: 1.5;\n}\n\n.cf-picker-list {\n  display: flex;\n  flex-direction: column;\n  gap: 2px;\n  /* Own scrollbar once the list gets long, instead of pushing the chart\n     and table further and further down the page (or, worse, clipping\n     silently the way the old nested-overflow bug did). */\n  max-height: 220px;\n  overflow-y: auto;\n  padding-right: 2px;\n}\n\n.cf-pick-row {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 6px 8px;\n  border-radius: 4px;\n  cursor: pointer;\n  font-size: 13px;\n  min-width: 0;\n}\n\n.cf-pick-row--sel {\n  background: var(--color-panel);\n}\n\n.cf-pick-row input[type='checkbox'] {\n  flex-shrink: 0;\n}\n\n.cf-color-dot {\n  width: 8px;\n  height: 8px;\n  border-radius: 50%;\n  flex-shrink: 0;\n}\n\n.cf-pick-label {\n  color: var(--color-text);\n  flex: 1;\n  min-width: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n}\n\n.cf-pick-model {\n  color: var(--color-text-dim);\n  font-size: 11px;\n  text-transform: uppercase;\n  flex-shrink: 0;\n}\n\n.cf-pick-active {\n  color: var(--color-accent);\n  font-size: 11px;\n  font-weight: 600;\n  flex-shrink: 0;\n}\n\n.cf-clear-row {\n  margin-top: 8px;\n}\n\n.cf-clear-btn {\n  background: none;\n  border: 1px solid var(--color-border);\n  border-radius: 4px;\n  color: var(--color-text-muted);\n  font-size: 12px;\n  padding: 4px 10px;\n  cursor: pointer;\n}\n.cf-clear-btn:hover {\n  border-color: var(--color-border-light);\n  color: var(--color-text);\n}\n\n.cf-select-prompt {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: 40px 16px;\n  color: var(--color-text-dim);\n  font-size: 13px;\n  text-align: center;\n}\n\n/* ─── Overlay chart ──────────────────────────────────────────────────── */\n\n.cf-warning {\n  font-size: 12px;\n  color: #d97706;\n  background: rgba(217, 119, 6, 0.08);\n  border: 1px solid var(--color-border);\n  border-radius: 4px;\n  padding: 6px 10px;\n  margin-bottom: 10px;\n  line-height: 1.5;\n}\n\n.cf-species-tabs {\n  display: flex;\n  gap: 4px;\n  flex-wrap: wrap;\n  margin-bottom: 10px;\n}\n\n.cf-species-tab {\n  padding: 3px 10px;\n  border-radius: 4px;\n  font-size: 12px;\n  cursor: pointer;\n  border: 1px solid var(--color-border);\n  background: transparent;\n  color: var(--color-text-muted);\n}\n.cf-species-tab:hover {\n  border-color: var(--color-border-light);\n  color: var(--color-text);\n}\n.cf-species-tab--active {\n  border-color: var(--color-accent);\n  background: var(--color-accent-glow);\n  color: var(--color-accent);\n}\n\n/* Fixed, predictable chart height at every zoom level and viewport width\n   — width still comes from ResponsiveContainer, only height is pinned. */\n.cf-chart-wrap {\n  width: 100%;\n  height: 320px;\n  min-width: 0;\n}\n@media (max-width: 900px) {\n  .cf-chart-wrap { height: 260px; }\n}\n\n.cf-footnote {\n  font-size: 11px;\n  color: var(--color-text-dim);\n  margin-top: 6px;\n  line-height: 1.5;\n}\n\n/* ─── Summary table ──────────────────────────────────────────────────── */\n\n.cf-table-wrap {\n  /* The one place a wide comparison (up to 4 fits, plus a growing list of\n     per-species R² rows) is allowed to overflow — with its own explicit\n     horizontal scrollbar, never a silent clip. */\n  overflow-x: auto;\n}\n\n.cf-table {\n  width: 100%;\n  border-collapse: collapse;\n  font-size: 12px;\n}\n\n.cf-table th,\n.cf-table td {\n  padding: 6px 10px;\n  border-bottom: 1px solid var(--color-border);\n  white-space: nowrap;\n}\n\n.cf-table th {\n  text-align: left;\n  font-weight: 600;\n}\n\n.cf-table td {\n  color: var(--color-text);\n  white-space: normal;\n}\n\n.cf-td-label {\n  color: var(--color-text-muted);\n  white-space: nowrap;\n}\n\n/* ─── Responsive ─────────────────────────────────────────────────────── */\n\n@media (max-width: 560px) {\n  .cf-picker-list { max-height: 160px; }\n  .cf-pick-model { display: none; } /* keep rows from wrapping awkwardly on narrow phones */\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
